import React, { useId } from 'react';
import { cn } from '../lib/utils';
import { ProductButton, ProductField, ProductSurface } from './ui/ProductUI';

/**
 * BrutalButton - Kept for API compatibility; rendered as the shared Linkit control.
 */
export const BrutalButton = ({ 
  children, 
  className, 
  variant = 'default', 
  size = 'md',
  shadow = 'brutalist',
  animation = 'none',
  onClick,
  'aria-label': ariaLabel,
  ...props 
}) => {
  const variants = {
    default: "bg-white text-slate-900 border-slate-900",
    pink: "bg-edge-pink text-white border-slate-900",
    coral: "bg-edge-coral text-white border-slate-900",
    amber: "bg-edge-amber text-edge-umber border-slate-900",
    indigo: "bg-indigo-600 text-white border-slate-900",
    danger: "bg-[#FF6B6B] text-white border-slate-900",
    dark: "bg-slate-900 text-white border-slate-900",
    ghost: "bg-transparent border-transparent shadow-none"
  };

  const sizes = {
    xs: "px-3 py-1.5 text-[9px]",
    sm: "px-4 py-2 text-[10px]",
    md: "px-6 py-3 text-xs",
    lg: "px-8 py-4 text-sm",
    xl: "px-10 py-5 text-sm"
  };

  const shadowClasses = {
    brutalist: "shadow-[0_14px_36px_rgba(32,39,50,0.10)]",
    coral: "shadow-[0_14px_36px_rgba(122,88,61,0.13)]",
    pink: "shadow-[0_14px_36px_rgba(111,63,85,0.13)]",
    amber: "shadow-[0_14px_36px_rgba(150,128,70,0.13)]",
    none: ""
  };

  const productVariants = {
    default: 'secondary',
    pink: 'primary',
    coral: 'primary',
    amber: 'secondary',
    indigo: 'primary',
    danger: 'danger',
    dark: 'primary',
    ghost: 'quiet',
  };

  return (
    <ProductButton
      onClick={onClick}
      aria-label={ariaLabel}
      variant={productVariants[variant] || 'secondary'}
      size={size === 'xs' ? 'sm' : size === 'xl' ? 'lg' : size}
      className={cn(
        variants[variant],
        sizes[size],
        shadowClasses[shadow],
        animation === 'misprint' && "anim-misprint",
        className
      )}
      {...props}
    >
      {children}
    </ProductButton>
  );
};

/**
 * BrutalCard - Shared premium surface.
 */
export const BrutalCard = ({
  children, 
  className, 
  shadow = 'brutalist', 
  hover = true, 
  onClick,
  ...props 
}) => {
  const shadowClasses = {
    brutalist: "shadow-[0_18px_48px_rgba(32,39,50,0.10)]",
    coral: "shadow-[0_18px_48px_rgba(122,88,61,0.12)]",
    pink: "shadow-[0_18px_48px_rgba(111,63,85,0.12)]",
    amber: "shadow-[0_18px_48px_rgba(150,128,70,0.12)]",
    none: ""
  };

  const hoverClasses = hover ? "hover:-translate-y-1 hover:shadow-[0_22px_55px_rgba(32,39,50,0.12)]" : "";

  return (
    <ProductSurface
      as="div"
      onClick={onClick}
      interactive={hover}
      className={cn(
        shadowClasses[shadow],
        hoverClasses,
        onClick && "cursor-pointer",
        className
      )}
      {...props}
    >
      {children}
    </ProductSurface>
  );
};

/**
 * BrutalPanel - Static layout container for forms, dashboards, and grouped content.
 */
export const BrutalPanel = ({
  children,
  className,
  as: Component = 'div',
  shadow = 'brutalist',
  ...props
}) => {
  const shadowClasses = {
    brutalist: "shadow-[0_18px_48px_rgba(32,39,50,0.10)]",
    coral: "shadow-[0_18px_48px_rgba(122,88,61,0.12)]",
    pink: "shadow-[0_18px_48px_rgba(111,63,85,0.12)]",
    amber: "shadow-[0_18px_48px_rgba(150,128,70,0.12)]",
    dark: "shadow-[0_18px_48px_rgba(15,23,42,0.16)]",
    none: ""
  };

  return (
    <ProductSurface
      as={Component}
      className={cn(shadowClasses[shadow], className)}
      {...props}
    >
      {children}
    </ProductSurface>
  );
};

/**
 * BrutalLabel - Editorial metadata marker.
 */
export const BrutalLabel = ({ children, className, variant = 'dark' }) => {
  const variants = {
    dark: "bg-slate-900 text-white",
    pink: "bg-edge-pink text-white",
    coral: "bg-edge-coral text-white",
    amber: "bg-edge-amber text-edge-umber",
    indigo: "bg-indigo-600 text-white",
    white: "bg-white text-slate-900 border-2 border-slate-900"
  };

  return (
    <span className={cn(
      "rounded-full px-3 py-1.5 text-[9px] font-bold tracking-[0.12em] uppercase",
      variants[variant],
      className
    )}>
      {children}
    </span>
  );
};

/**
 * BrutalInput - Shared Linkit field.
 */
export const BrutalInput = ({ className, label, shadow = 'coral', id, as = 'input', ...props }) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const Component = as;
  const shadowClasses = {
    coral: "focus:shadow-[0_0_0_4px_rgba(122,88,61,0.12)]",
    pink: "focus:shadow-[0_0_0_4px_rgba(111,63,85,0.12)]",
    amber: "focus:shadow-[0_0_0_4px_rgba(150,128,70,0.12)]",
    indigo: "focus:shadow-[0_0_0_4px_rgba(79,70,229,0.12)]",
    dark: "focus:shadow-[0_0_0_4px_rgba(15,23,42,0.10)]",
    none: ""
  };

  return (
    <ProductField
      as={Component}
      id={inputId}
      label={label}
      inputClassName={cn(shadowClasses[shadow], className)}
      {...props}
    />
  );
};
